import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, Tabs, Tab, Card, CardContent,
  Chip, IconButton, Alert, CircularProgress, Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Article as ArticleIcon,
  Lightbulb as LightbulbIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ArticleForm from '../components/expert/ArticleForm';
import * as articleApi from '../services/article';
import { Article, Innovation } from '../services/article';

const ExpertArticles = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [articles, setArticles] = useState<Article[]>([]);
  const [innovations, setInnovations] = useState<Innovation[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formType, setFormType] = useState<'article' | 'innovation'>('article');

  useEffect(() => {
    fetchMyContent();
  }, []);

  const fetchMyContent = async () => {
    try {
      setLoading(true);
      const [articlesData, innovationsData] = await Promise.all([
        articleApi.getMyArticles(),
        articleApi.getMyInnovations()
      ]);
      setArticles(articlesData);
      setInnovations(innovationsData);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (type: 'article' | 'innovation') => {
    setFormType(type);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleFormSuccess = () => {
    fetchMyContent();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const renderContentCard = (item: Article | Innovation, type: 'article' | 'innovation') => (
    <Grid item xs={12} md={6} key={item.id}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Chip
              label={getStatusLabel(item.status)}
              color={getStatusColor(item.status) as any}
              size="small"
            />
            {item.status === 'approved' && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/article/${type}/${item.id}`)}
              >
                <ViewIcon />
              </IconButton>
            )}
          </Box>

          <Typography variant="h6" gutterBottom>
            {item.title}
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            {item.summary}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {item.tags?.slice(0, 3).map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary">
            {item.date || new Date(item.createdAt!).toLocaleDateString()}
          </Typography>

          {item.status === 'rejected' && item.rejectionReason && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Rejection Reason:</strong> {item.rejectionReason}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={600}>
          My Content
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArticleIcon />}
            onClick={() => handleOpenForm('article')}
          >
            New Article
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<LightbulbIcon />}
            onClick={() => handleOpenForm('innovation')}
          >
            New Innovation
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label={`Articles (${articles.length})`} />
        <Tab label={`Innovations (${innovations.length})`} />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {articles.length === 0 ? (
            <Alert severity="info">
              You haven't submitted any articles yet. Click "New Article" to get started.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {articles.map((article) => renderContentCard(article, 'article'))}
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {innovations.length === 0 ? (
            <Alert severity="info">
              You haven't submitted any innovation spotlights yet. Click "New Innovation" to get started.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {innovations.map((innovation) => renderContentCard(innovation, 'innovation'))}
            </Grid>
          )}
        </Box>
      )}

      <ArticleForm
        open={formOpen}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        type={formType}
      />
    </Container>
  );
};

export default ExpertArticles;
